import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState, useContext, useMemo, memo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { twMerge } from 'tailwind-merge';
import { Icon, Spinner } from '@scale/scaleui';
import { nord } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/shared/codeBlockStyles';
import { CodeBlock } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import {
  LANGUAGE_OPTIONS as SUPPORTED_EXECUTION_LANGUAGES,
  fullLanguageMap,
} from '@scale/shared/sphereEngine/types';
import CodeBlockExecutionOutput from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/CodeBlockExecutionOutput';
import { CodeBlockCopyButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/shared/CodeBlockCopyButton';
import { CodingScratchpad } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/CodingScratchpad';
import useCodeExecutionState from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/hooks/useCodeExecutionState';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import crypto from 'crypto';
import { useCodeBlockStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useCodeBlockStore';
import { makeStyles } from 'design-system/product/theme';
import cn from 'classnames';

interface IExecutableCodeBlockProps {
  code: string;
  languageMatch?: RegExpExecArray | null;
  languageOverride?: string;
  codeBlocksInMarkdownText: CodeBlock[];
  codeBlockIndex: number;
  autoRunDependenciesComplete?: boolean;
  loading?: boolean;
  defaultExpanded?: boolean; // New prop to determine default expanded state
}

// Function to convert language identifier to a readable format
const getReadableLanguage = (language: string) => {
  return fullLanguageMap[language] || language.toUpperCase();
};

const NUM_LINES_COLLAPSED = 15;

export const ExecutableCodeBlock = memo(
  // eslint-disable-next-line complexity
  ({
    code,
    languageMatch,
    languageOverride,
    codeBlocksInMarkdownText,
    codeBlockIndex,
    autoRunDependenciesComplete,
    loading,
    defaultExpanded = true, // Default value for the new prop
  }: IExecutableCodeBlockProps) => {
    const classes = useStyles();

    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const language = languageOverride ?? languageMatch?.[1].toLowerCase() ?? '';
    const taskContext = useContext(ChatTaskContext);
    const customCodeEnvironments = taskContext?.codingConfig?.customCodeEnvironments;

    // We only allow code execution if there is exactly one custom code environment or if there's
    // no custom code environment AND the language is supported. If multiple custom code environments
    // exist, we don't allow code execution - contributors must use coding scratchpad to execute code instead.
    const canExecuteCode =
      customCodeEnvironments?.length === 1 ||
      ((!customCodeEnvironments || customCodeEnvironments.length === 0) &&
        !!SUPPORTED_EXECUTION_LANGUAGES.find(({ id }) => id === language));

    // Hashing is an expensive computation that we don't want to perform every time
    // `code` changes as it gets streamed in fast via MRS.
    const storeKey = useMemo(() => {
      if (loading) {
        return '';
      }

      return crypto
        .createHash('sha256')
        .update(`${code}-${language}-executableCodeBlock`)
        .digest('hex');
    }, [code, language, loading]);

    const { getScratchpadOpenState, setScratchpadOpenState } = useCodeBlockStore();
    const [scratchpadOpen, setScratchpadOpen] = [
      getScratchpadOpenState(storeKey),
      (open: boolean) => setScratchpadOpenState(storeKey, open),
    ];

    const {
      outputTabOpen,
      outputValue,
      tab,
      isFetching,
      fetchError,
      loadingTaskIdForLogging,
      handleExecuteCode,
      setOutputTabOpen,
      setTab,
    } = useCodeExecutionState({
      storeKey,
      code,
      language,
      customCodeEnvironment: customCodeEnvironments?.[0],
    });

    const toggleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    useEffect(() => {
      const autoRunExecutableCodeBlocks =
        taskContext.codingConfig?.autoRunExecutableCodeBlocks ??
        taskContext.generalMarkdownConfig?.autoRunExecutableCodeBlocks;
      if (autoRunDependenciesComplete && canExecuteCode && autoRunExecutableCodeBlocks) {
        handleExecuteCode();
      }
    }, [
      autoRunDependenciesComplete,
      taskContext.codingConfig,
      taskContext.generalMarkdownConfig,
      canExecuteCode,
    ]);

    const numLines = code.split(/\r\n|\r|\n/).length;

    return (
      <>
        <div
          className={twMerge(
            'group bg-transparent relative px-0 py-0 scale-theme',
            isExpanded ? 'overflow-auto' : 'overflow-hidden',
          )}
        >
          <div className="text-white text-xs font-medium font-mono px-3 flex items-center justify-between bg-neutral-800 rounded-t-md py-1.5">
            <span className="text-neutral-0">{getReadableLanguage(language)}</span>
            <div className="flex items-center justify-end">
              {!loading && canExecuteCode && (
                <div className="pr-1">
                  {isFetching ? (
                    <Spinner className="pr-2" size="xs" variant="primary" />
                  ) : (
                    <Icon
                      icon={['far', 'play']}
                      className="hover:bg-neutral-700/40"
                      variant="white"
                      onClick={e => {
                        e.stopPropagation();
                        handleExecuteCode();
                      }}
                      disabled={isFetching || loadingTaskIdForLogging}
                      tooltip="Run Code"
                      tooltipProps={{ side: 'top', delayDuration: 100 }}
                      data-testid="run-code-button"
                    />
                  )}
                </div>
              )}
              {!loading && customCodeEnvironments && customCodeEnvironments?.length > 1 && (
                <Icon
                  icon={['far', 'exclamation-circle']}
                  className="hover:bg-neutral-800"
                  variant="white"
                  tooltip="Please use the coding scratchpad (click on the pencil icon to the right) to execute and test your code."
                  tooltipProps={{ side: 'top', delayDuration: 100 }}
                />
              )}
              {!loading && (
                <div className="pr-1">
                  <Icon
                    icon={['far', 'pen']}
                    className="hover:bg-neutral-700/40"
                    variant="white"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setScratchpadOpen(true);
                    }}
                    tooltip="Scratchpad (changes made are not saved)"
                    tooltipProps={{ side: 'top', delayDuration: 100 }}
                  />
                </div>
              )}
              <div className="pl-2 pr-3 pt-1">
                <CodeBlockCopyButton code={code} />
              </div>
              {numLines > NUM_LINES_COLLAPSED && isExpanded && (
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Collapse
                  <FontAwesomeIcon icon="chevron-up" className="ml-1" />
                </button>
              )}
            </div>
          </div>
          <div>
            <div
              className={cn(
                'p-3 bg-neutral-900',
                isExpanded
                  ? 'max-h-[900px] overflow-auto rounded-b-md'
                  : 'max-h-[290px] overflow-hidden',
                classes.codeblockStyle,
              )}
            >
              <SyntaxHighlighter
                // eslint-disable-next-line react/no-children-prop
                children={code}
                // @ts-expect-error TODO: check how to type this - React.CCSProperties is the type specified in the library but it doesn't work
                style={nord}
                language={language}
                PreTag="div"
                CodeTag="div"
                showLineNumbers={true}
              />
            </div>
            {numLines > NUM_LINES_COLLAPSED && !isExpanded && (
              <div className="flex items-center justify-center py-2 text-white text-xs font-medium font-mono bg-neutral-900 rounded-b-md">
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 text-white rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Expand
                  <FontAwesomeIcon icon="chevron-down" className="ml-1" />
                </button>
              </div>
            )}
          </div>
          {language !== 'text' && outputTabOpen && (
            <div className="border-none pt-4" onClick={e => e.stopPropagation()}>
              <CodeBlockExecutionOutput
                className="w-full h-40 p-0 whitespace-pre-line"
                outputValue={outputValue ?? ''}
                setOutputTabOpen={setOutputTabOpen}
                tab={tab}
                setTab={setTab}
                fetchError={fetchError as boolean}
                fullScreenEnabled={false}
                fullWidthEnabled={true}
              />
            </div>
          )}
          {scratchpadOpen && (
            <div className="fixed top-0 left-0 w-full h-full bg-utility-black/50 flex flex-col justify-center items-center z-[100]">
              <CodingScratchpad
                rawCode={code}
                originalLanguage={language}
                codeBlocksInMarkdownText={codeBlocksInMarkdownText}
                setOpen={setScratchpadOpen}
                codeBlockIndex={codeBlockIndex}
                customCodeEnvironments={customCodeEnvironments}
              />
            </div>
          )}
        </div>
      </>
    );
  },
);

ExecutableCodeBlock.displayName = 'ExecutableCodeBlock';

export default ExecutableCodeBlock;

const useStyles = makeStyles(() => ({
  codeblockStyle: {
    '& div[class*="language-"]': {
      color: 'rgb(248, 248, 242)',
      background: 'none',
      fontFamily: '"Fira Code", Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
      textAlign: 'left',
      whiteSpace: 'pre-wrap !important',
      wordSpacing: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      lineHeight: '1.5',
      tabSize: 4,
      hyphens: 'none',
      fontSize: '10px',
      width: '800px !important',
    },
    '& div': {
      scrollbarColor: 'rgba(248, 248, 242, 0.5) rgba(0, 0, 0, 0)',
      scrollbarWidth: 'thin',
    },
  },
}));
